import { CircleFragment, RoleSummaryFragment } from '../gql'
import { getProposalVoterCircleId } from './getProposalVoterCircleId'

// Minimal shape needed from OrgData, so the rule can be tested on its own and
// shared by the webapp and the backend — the two must agree, or the screen lets
// someone prepare a change the server will refuse.
interface CircleLookup {
  getCircle(id: string | undefined): CircleFragment | undefined
  getRole(id: string | undefined): RoleSummaryFragment | undefined
  ownerCircleOf(circle: CircleFragment): CircleFragment | undefined
  childrenOf(circleId: string): readonly CircleFragment[]
}

// Which circles a tension may propose changes to (Yocoop, 09/08/2026).
//
// One rule governs both sides now: you prepare changes where you will vote. The
// perimeter is therefore the deciding circle of `getProposalVoterCircleId` —
// never a plain role the tension hangs on, since a role does not redefine
// itself.
//
// A LINK is the one ambiguous case, and it is ambiguous by nature: it belongs to
// two circles at once, the one it sits in and the one it represents, and its
// holder acts in both. Nothing in the data says which one the tension is about.
// So both are returned, and the choice is settled later by what the author
// actually modifies — never by asking them to pick, which would let them choose
// the friendlier room (the very thing D6 closed for `votersScope`).
//
// A CIRCLE (a role holding roles) opens two rooms the same way (#121,
// 24/09/2026): its own roles change what it holds, decided by the circle
// itself, while the circle's own definition is still decided by the circle
// above — a role does not redefine itself. So a role of C may propose on the
// roles of C from C, but not on C itself; the first change settles which.
//
// Order carries meaning: the closest circle comes first — the one a link sits
// in, or the circle itself.
export function getProposalScope(
  orgData: CircleLookup,
  circleId: string
): string[] {
  const circle = orgData.getCircle(circleId)
  // Archived or deleted while the tension was being written: no perimeter at
  // all, rather than an exception in the middle of the editor.
  if (!circle) return []

  const role = orgData.getRole(circle.roleId)
  if (role?.parentLink) {
    const candidates = [
      orgData.getCircle(circle.parentId || undefined)?.id,
      // For a link, ownerCircleOf already climbs to the grandparent.
      orgData.ownerCircleOf(circle)?.id,
    ]
    // A link directly under the root has no grandparent: both lookups land on
    // the same circle, and one candidate is the honest answer.
    return [...new Set(candidates.filter((id): id is string => !!id))]
  }

  const owner = getProposalVoterCircleId(orgData, circleId)
  const isCircle =
    role?.singleMember === false && orgData.childrenOf(circle.id).length > 0
  return isCircle && owner !== circle.id ? [circle.id, owner] : [owner]
}

// The circle that votes on a proposal. A settled `decidingCircleId` is written
// by the author's own draft, so it is only trusted when it is one of the
// tension's candidates: otherwise an author could send the vote to a friendlier
// room, which is what D6 closed (#121 review, 25/09/2026). Server and app both
// read it through here.
export function getDecidingCircleId(
  orgData: CircleLookup,
  circleId: string,
  settledCircleId?: string | null
): string {
  if (
    settledCircleId &&
    getProposalScope(orgData, circleId).includes(settledCircleId)
  ) {
    return settledCircleId
  }
  return getProposalVoterCircleId(orgData, circleId)
}
